"use client";

import {
  Button,
  Divider,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui";

import { ReactNode } from "react";
import { HiArrowRightOnRectangle } from "react-icons/hi2";

import Tooltip from "@/components/ui/tooltip";

import { formatName } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";

function initialsOf(firstName?: string | null, lastName?: string | null) {
  const parts = [firstName, lastName].filter(Boolean) as string[];
  if (parts.length === 0) return "?";
  return parts.map((n) => n[0]!.toUpperCase()).join("");
}

function displayNameOf(firstName?: string | null, lastName?: string | null) {
  if (!firstName && !lastName) return "Profile";
  return `${firstName ?? ""}${lastName ? ` ${lastName}` : ""}`.trim();
}

function PatientAvatar({
  firstName,
  lastName,
  className,
}: {
  firstName?: string | null;
  lastName?: string | null;
  className?: string;
}) {
  const initials = initialsOf(firstName, lastName);
  const name = displayNameOf(firstName, lastName);
  const base = cx(
    "flex h-9 w-9 items-center justify-center rounded-full bg-primary text-caption-emphasis text-primary-foreground transition-opacity hover:opacity-80",
    className,
  );

  const inner = <span>{initials}</span>;
  const trigger = (
    <div className={base} aria-label={name}>
      {inner}
    </div>
  );

  return (
    <Tooltip
      content={name}
      side="bottom"
      triggerAsChild
      className="text-caption"
    >
      {trigger}
    </Tooltip>
  );
}

/**
 * Presentational avatar + sign-out pair used in patient-facing headers.
 * Consumers wire their own auth hook (`usePatientPortal` or `usePatientClaim`)
 * and pass identity + a sign-out callback — this component knows nothing
 * about auth state.
 */
export function PatientProfileBadge({
  firstName,
  lastName,
  onSignOut,
  className,
  children,
}: {
  firstName?: string | null;
  lastName?: string | null;
  onSignOut: () => void | Promise<void>;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <div className={cx("flex shrink-0 items-center gap-2", className)}>
      {children}
      <Popover>
        <PopoverTrigger>
          <PatientAvatar firstName={firstName} lastName={lastName} />
        </PopoverTrigger>

        <PopoverContent className="w-fit min-w-0 space-y-2" align="end">
          Hey, <span>{formatName(firstName, lastName)}</span>!
          <Divider />
          <Button
            variant="secondary"
            icon={<HiArrowRightOnRectangle />}
            className="w-full"
            size="sm"
            onClick={() => void onSignOut()}
          >
            Sign out
          </Button>
        </PopoverContent>
      </Popover>
    </div>
  );
}
